import { useMemo } from 'react';
import {
  getMigrationDaysRemaining,
  isMigratedPaidUser,
} from 'utils/billing/conversion';
import {
  BILLING_STATUS_CTAS,
  BILLING_STATUS_KEYS,
  BILLING_STATUS_VARIANTS,
  deriveBillingStatus,
  type BillingStatusVM,
} from 'utils/billing/deriveBillingStatus';
import { useAccountBilling } from './useAccountBilling';
import { useAccountInfo } from './useAccountInfo';
import { usePricingConfig } from './usePricingConfig';
import { useQuota } from './useQuota';
import { useSelfServeGate } from './useSelfServeGate';

/**
 * View-model for the top-bar billing status pill.
 *
 * Trial state comes from /account-info (D22), the same source as the sidebar
 * trial widget, so the two surfaces never disagree.
 *
 * @returns The billing status view-model, or `undefined` when the self-serve
 * gate is closed, quota is unavailable, or there is nothing to show (so the
 * pill renders nothing for non-self-serve users).
 */
export const useBillingStatus = (): BillingStatusVM | undefined => {
  const { allowed } = useSelfServeGate();
  const { data: quota } = useQuota();
  const { data: accountInfo } = useAccountInfo();
  const { isTrialLapsed } = useAccountBilling();
  const cfg = usePricingConfig();

  return useMemo(() => {
    if (isMigratedPaidUser(accountInfo)) {
      const daysRemaining = getMigrationDaysRemaining(
        accountInfo?.migrationEndDate,
      );

      if (daysRemaining === undefined || daysRemaining === 0) {
        return undefined;
      }

      const isLastDay = daysRemaining <= 1;
      const isEndingSoon = daysRemaining <= cfg.trialEndingThresholdDays;

      return {
        key: isLastDay
          ? BILLING_STATUS_KEYS.migrationLastDay
          : BILLING_STATUS_KEYS.migration,
        interpolation: isLastDay ? undefined : { count: daysRemaining },
        variant: isEndingSoon
          ? BILLING_STATUS_VARIANTS.danger
          : BILLING_STATUS_VARIANTS.emphasis,
        cta: BILLING_STATUS_CTAS.movePlan,
      };
    }

    if (!allowed || !quota) return undefined;

    if (isTrialLapsed) {
      return {
        key: BILLING_STATUS_KEYS.trialEnded,
        interpolation: undefined,
        variant: 'danger',
        cta: BILLING_STATUS_CTAS.seePlan,
      };
    }

    return deriveBillingStatus(
      quota,
      cfg,
      accountInfo?.trial ?? undefined,
      accountInfo?.tier ?? undefined,
    );
  }, [allowed, quota, accountInfo, isTrialLapsed, cfg]);
};
